import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface SelectContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: string;
  onChange: (value: string) => void;
}

const SelectContext = createContext<SelectContextType | undefined>(undefined);

export const Select: React.FC<{
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}> = ({ value, onValueChange, children }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <SelectContext.Provider value={{ open, setOpen, value, onChange: onValueChange }}>
      <div className="relative inline-block w-full sm:w-auto" ref={containerRef}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

export const SelectTrigger: React.FC<{
  className?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ className = '', icon, children }) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectTrigger must be used within Select');

  return (
    <button
      type="button"
      onClick={() => context.setOpen(!context.open)}
      className={`flex items-center gap-2.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all hover:bg-white/10 ${className}`}
    >
      {icon && <span className="text-gray-400 group-hover:text-white transition-colors">{icon}</span>}
      <div className="flex-1 text-left">
        {children}
      </div>
      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${context.open ? 'rotate-180' : ''}`} />
    </button>
  );
};

export const SelectValue: React.FC<{ placeholder?: string; children?: React.ReactNode }> = ({ placeholder, children }) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectValue must be used within Select');
  return <span className="truncate block">{children || context.value || placeholder}</span>;
};

export const SelectContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectContent must be used within Select');

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setCanScrollUp(scrollTop > 0);
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 1);
    }
  };

  useEffect(() => {
    if (context.open) {
      // Check initially after animation/render
      const timer = setTimeout(checkScroll, 50);
      return () => clearTimeout(timer);
    }
  }, [context.open, children]);

  const scroll = (direction: 'up' | 'down') => {
    if (scrollRef.current) {
      const scrollAmount = 100;
      scrollRef.current.scrollBy({
        top: direction === 'up' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <AnimatePresence>
      {context.open && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -5 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -5 }}
          transition={{ duration: 0.1, ease: 'easeOut' }}
          className="absolute z-50 mt-2 min-w-full bg-[#1a1a1a] border border-white/10 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col backdrop-blur-md"
        >
          {canScrollUp && (
            <button 
              onClick={(e) => { e.stopPropagation(); scroll('up'); }}
              className="flex items-center justify-center py-1.5 text-gray-500 hover:text-white hover:bg-white/5 transition-colors border-b border-white/5 z-10"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
          )}
          
          <div 
            ref={scrollRef}
            onScroll={checkScroll}
            className="overflow-y-auto max-h-60 py-1 scrollbar-hide"
          >
            {children}
          </div>

          {canScrollDown && (
            <button 
              onClick={(e) => { e.stopPropagation(); scroll('down'); }}
              className="flex items-center justify-center py-1.5 text-gray-500 hover:text-white hover:bg-white/5 transition-colors border-t border-white/5 z-10"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const SelectItem: React.FC<{
  value: string;
  children: React.ReactNode;
}> = ({ value, children }) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectItem must be used within Select');

  const isSelected = context.value === value;

  return (
    <button
      type="button"
      onClick={() => {
        context.onChange(value);
        context.setOpen(false);
      }}
      className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-3 ${
        isSelected ? 'bg-blue-600/10 text-blue-400 font-bold' : 'text-gray-300 hover:bg-white/5'
      }`}
    >
      <div className="w-4 h-4 flex items-center justify-center shrink-0">
        {isSelected && <Check className="w-3.5 h-3.5" />}
      </div>
      <span className="flex-1">{children}</span>
    </button>
  );
};
